import React, { useCallback, useEffect } from "react";
import { goToNext, goPrevious, useFormValue, useFieldAutoFocus } from '../common';
import { FormLabel } from "./FormLabel";
import styled from "styled-components";
import { Button, Icon, Progress } from "semantic-ui-react";
import { useFluidForm } from "../FormContext";
import { useFluidFormClient } from "../../../fluidForm.client";
import { useDropzone } from "react-dropzone";
import "../common-form-styles.css";
// Styled components
const DropzoneContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isDragActive', 'isFocused'].includes(prop),
}) `
  border: 2px dashed ${props => props.isDragActive ? '#3b82f6' : '#d1d5db'};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isDragActive ? '#eff6ff' : props.isFocused ? '#f8fafc' : '#fafafa'};
  border-color: ${props => props.isDragActive || props.isFocused ? '#3b82f6' : '#d1d5db'};

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;
const FilePreview = styled.div `
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
`;
const FileDetails = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const FileIcon = styled.div `
  width: 40px;
  height: 40px;
  background: #3b82f6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.75rem;
`;
const FileInfo = styled.div `
  flex: 1;
`;
const FileName = styled.div `
  font-weight: 500;
  color: #1f2937;
`;
const FileSize = styled.div `
  font-size: 0.875rem;
  color: #6b7280;
`;
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
`;
const ErrorMessage = styled.div `
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;
export function FieldFileComponent({ fieldPath, options }) {
    const fluidForm = useFluidForm();
    const field = fluidForm.getFieldRenderView(fieldPath);
    const config = fluidForm.getFieldConfigByFieldPath(fieldPath);
    const ref = React.useRef(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [uploadError, setUploadError] = React.useState(null);
    const formKey = fluidForm.getFormKey();
    const fluidFormClient = useFluidFormClient();
    const omitLabel = options?.omitLabel || false;
    useFieldAutoFocus(ref, field.fieldPath, options?.autoFocus);
    const [value, setValue] = useFormValue(field.fieldPath);
    const fileValue = value;
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    const getFileExtension = (fileName) => {
        if (!fileName) {
            return 'FILE';
        }
        return fileName.split('.').pop()?.toUpperCase() || 'FILE';
    };
    const uploadFileToStorage = async (file) => {
        try {
            setIsUploading(true);
            setUploadProgress(0);
            setUploadError(null);
            const uploadData = await fluidFormClient.createUploadUrl(formKey, field.fieldPath, file.name, file.type);
            setUploadProgress(25);
            await fluidFormClient.uploadFile(file, uploadData.signedUrl, field.fieldPath);
            setUploadProgress(75);
            // Create file info object
            const fileInfo = {
                fileName: file.name,
                mimeType: file.type,
                fileUri: uploadData.fileUri,
            };
            setUploadProgress(100);
            return fileInfo;
        }
        catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        }
        finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };
    const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
        // Handle rejected files
        if (rejectedFiles.length > 0) {
            const errors = rejectedFiles[0].errors;
            const errorMessage = errors.map((error) => error.message).join(', ');
            setUploadError(errorMessage);
            return;
        }
        // Handle accepted files
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0]; // Only handle the first file
            try {
                const fileInfo = await uploadFileToStorage(file);
                setValue(fileInfo);
                setUploadError(null);
            }
            catch (error) {
                setUploadError('Failed to upload file. Please try again.');
            }
        }
    }, [fluidFormClient, fluidForm, setValue]);
    // Configure dropzone
    const { getRootProps, getInputProps, isDragActive, isFocused } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/*': ['.txt', '.csv']
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        multiple: false,
        disabled: isUploading
    });
    const handleRemoveFile = async () => {
        try {
            await fluidFormClient.removeAttachment(fluidForm.getFormKey(), field.fieldPath);
            setValue(null);
            setUploadError(null);
        }
        catch (error) {
            setUploadError('Failed to remove file from storage. Please try again.');
        }
    };
    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (fluidForm.currentField === field.fieldPath) {
                if (event.key === "Tab") {
                    event.preventDefault();
                    goToNext(field.fieldPath, fluidForm);
                    return;
                }
                if (event.key === "Backspace" && !fileValue) {
                    event.preventDefault();
                    goPrevious(field.fieldPath, fluidForm);
                    return;
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [fluidForm.currentField, field.fieldPath, fileValue, fluidForm]);
    const handleOpenFile = async () => {
        try {
            const response = await fluidFormClient.getAttachmentSignedUrl(formKey, fileValue.fileUri, field.fieldPath);
            window.open(response.signedUrl, '_blank');
        }
        catch (error) {
            setUploadError('Failed to load the requested file. Please try again.');
        }
    };
    return (React.createElement("div", { ref: ref },
        !omitLabel && React.createElement(FormLabel, { config: config, options: options, fieldPath: fieldPath }),
        !fileValue && !isUploading && (React.createElement(DropzoneContainer, { ...getRootProps(), isDragActive: isDragActive, isFocused: isFocused || fluidForm.isFocused(field.fieldPath), onFocus: () => fluidForm.setCurrentField(field.fieldPath), onBlur: () => fluidForm.unfocusField(field.fieldPath) },
            React.createElement("input", { ...getInputProps() }),
            React.createElement(Icon, { name: "cloud upload", size: "big", color: "blue" }),
            React.createElement("div", { style: { marginTop: '1rem' } },
                React.createElement("div", { style: { fontSize: '1.1rem', fontWeight: 500, marginBottom: '0.5rem' } }, isDragActive ? 'Drop the file here...' : 'Drop your file here or click to browse'),
                React.createElement("div", { style: { fontSize: '0.875rem', color: '#6b7280' } }, "Supports images, PDFs, and documents up to 10MB")))),
        isUploading && (React.createElement(DropzoneContainer, { onFocus: () => fluidForm.setCurrentField(field.fieldPath), onBlur: () => fluidForm.unfocusField(field.fieldPath) },
            React.createElement(Icon, { name: "cloud upload", size: "big", color: "blue" }),
            React.createElement("div", { style: { marginTop: '1rem' } },
                React.createElement("div", { style: { fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem' } }, "Uploading file..."),
                React.createElement(Progress, { percent: uploadProgress, indicating: true })))),
        fileValue && !isUploading && (React.createElement(FilePreview, { onFocus: () => fluidForm.setCurrentField(field.fieldPath), onBlur: () => fluidForm.unfocusField(field.fieldPath) },
            React.createElement(FileDetails, null,
                React.createElement(FileIcon, null, getFileExtension(fileValue.fileName)),
                React.createElement(FileInfo, null,
                    React.createElement(FileName, null, fileValue.fileName)),
                React.createElement(ActionButtons, null,
                    React.createElement(Button, { icon: "external", size: "small", onClick: handleOpenFile, title: "Open file" }),
                    React.createElement(Button, { icon: "trash", color: "red", size: "small", onClick: handleRemoveFile, title: "Remove file" }))))),
        uploadError && (React.createElement(ErrorMessage, null, uploadError))));
}
